import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { useAuth0SessionUser } from '@hooks/useAuth0SessionUser';
import { withAccessTokenLock } from '@lib/auth0/access-token-lock';
import { ACCESS_TOKEN_ROUTE } from '@lib/auth0/access-token-route';
import { triggerToast } from '@components/atoms/toast';

let sessionExpiryToastShown = false;

const HTTP_UNAUTHORIZED = 401;

export const ACCESS_TOKEN_QUERY_KEY = ['access-token'] as const;

// Long enough to collapse the burst of consumers mounting on a page load
// into a single request, short enough that a token refreshed elsewhere
// (e.g. a forced session refresh) is picked up promptly.
const ACCESS_TOKEN_STALE_TIME_IN_MS = 60 * 1000;

export const useAccessToken = (): {
  accessToken: string | null;
  accessTokenIsFetching: boolean;
} => {
  const { user, isLoading: isUserLoading } = useAuth0SessionUser();
  const router = useRouter();

  const fetchToken = async (): Promise<string | null> => {
    try {
      const response = await withAccessTokenLock(() =>
        fetch(ACCESS_TOKEN_ROUTE)
      );
      if (!response.ok) {
        // FFH-3019: this branch never ran. It waited on a `requiresAuth` flag
        // that the SDK's endpoint does not send, and the body it read first
        // threw on any non-JSON error, which the catch below then swallowed.
        // A 401 from the token route is a settled "the session is gone", so it
        // is the status that decides, and the body is only read for its
        // message.
        if (response.status === HTTP_UNAUTHORIZED) {
          if (!sessionExpiryToastShown) {
            sessionExpiryToastShown = true;
            triggerToast({
              title: 'Error',
              subTitle: 'Your session has expired, please login again',
              variant: 'error'
            });
          }
          router.push('/auth/logout');
          return null;
        }

        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to fetch access token');
      }
      const data = await response.json();
      return data.token || null;
    } catch (error) {
      console.error('Error fetching access token:', error);
      return null;
    }
  };

  // The query is shared across every consumer of this hook, so mounting
  // several token-hungry components triggers one request instead of one
  // each. Errors resolve to null rather than throwing so the query settles
  // and consumers see { accessToken: null, accessTokenIsFetching: false },
  // matching the pre-react-query behaviour. For logged-out users the query
  // stays disabled and accessTokenIsFetching stays true forever — several
  // consumers deliberately rely on that quirk, do not "fix" it here.
  const { data: accessToken, isLoading: accessTokenIsFetching } = useQuery({
    queryKey: [...ACCESS_TOKEN_QUERY_KEY, user?.sub],
    queryFn: fetchToken,
    enabled: !isUserLoading && Boolean(user),
    staleTime: ACCESS_TOKEN_STALE_TIME_IN_MS,
    refetchOnWindowFocus: false,
    retry: false
  });

  return {
    accessToken: accessToken ?? null,
    accessTokenIsFetching
  };
};
